import * as React from "react";
import { Trash2, TriangleAlert } from "lucide-react";

import type { Brand } from "@/data/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Destructive confirm for deleting a brand: the owner must type the brand's
 *  exact name before the delete button enables. Open when `brand` is non-null. */
export function DeleteBrandDialog({
  brand,
  onOpenChange,
  onConfirm,
}: {
  brand: Brand | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (brand: Brand) => void;
}) {
  const [value, setValue] = React.useState("");

  // Clear the typed name whenever the target brand changes (or the dialog reopens).
  React.useEffect(() => {
    setValue("");
  }, [brand?.id]);

  const confirmed = !!brand && value.trim() === brand.name;

  return (
    <Dialog open={brand !== null} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-border-strong bg-popover shadow-portal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="size-[18px] text-[#ff7a7a]" />
            Delete brand
          </DialogTitle>
          <DialogDescription>
            This permanently deletes{" "}
            <span className="font-semibold text-foreground">{brand?.name}</span>, all of its themes
            and members. This can’t be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[7px]">
          <Label htmlFor="confirm-brand-name">
            Type <span className="font-semibold text-foreground">{brand?.name}</span> to confirm
          </Label>
          <Input
            id="confirm-brand-name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            autoFocus
            placeholder={brand?.name}
            onKeyDown={(e) => {
              if (e.key === "Enter" && confirmed && brand) onConfirm(brand);
            }}
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            size="sm"
            disabled={!confirmed}
            onClick={() => {
              if (brand && confirmed) onConfirm(brand);
            }}
          >
            <Trash2 className="size-4" /> Delete brand
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
